import { AffineMatrix } from "./types";

interface GCPInput {
  imageX: number;
  imageY: number;
  mapLng: number;
  mapLat: number;
}

/** Convert latitude (degrees) to normalised Web Mercator Y (0 at top, 1 at bottom). */
function mercatorYFromLat(lat: number): number {
  return (180 - (180 / Math.PI) * Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360))) / 360;
}

/** Convert normalised Web Mercator Y back to latitude (degrees). */
function latFromMercatorY(y: number): number {
  return (360 / Math.PI) * Math.atan(Math.exp(((180 - y * 360) * Math.PI) / 180)) - 90;
}

/**
 * Compute the affine transformation matrix from pixel coordinates to Mercator coordinates.
 *
 * Solves for 6 unknowns:
 *   lng       = a*x + b*y + c
 *   mercatorY = d*x + e*y + f
 *
 * Fitting in Mercator space matches how Mapbox interpolates pixels between
 * projected corners, avoiding positional drift at interior points.
 *
 * Uses a QR decomposition to solve the least-squares problem without forming A^T*A.
 *
 * Returns null if fewer than 3 GCPs are provided or the system is singular (e.g. collinear points).
 */
export function computeAffineTransform(gcps: GCPInput[]): AffineMatrix | null {
  if (gcps.length < 3) return null;

  const meanX = gcps.reduce((sum, gcp) => sum + gcp.imageX, 0) / gcps.length;
  const meanY = gcps.reduce((sum, gcp) => sum + gcp.imageY, 0) / gcps.length;
  const scaleX = Math.sqrt(gcps.reduce((sum, gcp) => sum + (gcp.imageX - meanX) ** 2, 0) / gcps.length);
  const scaleY = Math.sqrt(gcps.reduce((sum, gcp) => sum + (gcp.imageY - meanY) ** 2, 0) / gcps.length);
  if (scaleX === 0 || scaleY === 0) return null;

  const columns = [gcps.map((gcp) => (gcp.imageX - meanX) / scaleX), gcps.map((gcp) => (gcp.imageY - meanY) / scaleY), gcps.map(() => 1)];
  const solutions = solveLeastSquares(columns, [gcps.map((gcp) => gcp.mapLng), gcps.map((gcp) => mercatorYFromLat(gcp.mapLat))]);
  if (!solutions) return null;

  const [lngCoefficients, latCoefficients] = solutions;
  const a = lngCoefficients[0] / scaleX;
  const b = lngCoefficients[1] / scaleY;
  const d = latCoefficients[0] / scaleX;
  const e = latCoefficients[1] / scaleY;

  return {
    a,
    b,
    c: lngCoefficients[2] - a * meanX - b * meanY,
    d,
    e,
    f: latCoefficients[2] - d * meanX - e * meanY,
  };
}

function solveLeastSquares(columns: number[][], targets: number[][]): number[][] | null {
  const orthonormalColumns: number[][] = [];
  const upperTriangle = columns.map(() => columns.map(() => 0));
  const rankTolerance = 1e-12;

  for (let columnIndex = 0; columnIndex < columns.length; columnIndex += 1) {
    const column = [...columns[columnIndex]];
    const originalNorm = Math.sqrt(column.reduce((sum, value) => sum + value * value, 0));

    for (let previousIndex = 0; previousIndex < columnIndex; previousIndex += 1) {
      const previousColumn = orthonormalColumns[previousIndex];
      const projection = previousColumn.reduce((sum, value, index) => sum + value * column[index], 0);
      upperTriangle[previousIndex][columnIndex] = projection;
      column.forEach((value, index) => {
        column[index] = value - projection * previousColumn[index];
      });
    }

    const norm = Math.sqrt(column.reduce((sum, value) => sum + value * value, 0));
    if (norm <= originalNorm * rankTolerance) return null;

    upperTriangle[columnIndex][columnIndex] = norm;
    orthonormalColumns.push(column.map((value) => value / norm));
  }

  return targets.map((target) => {
    const projectedTarget = orthonormalColumns.map((column) => column.reduce((sum, value, index) => sum + value * target[index], 0));
    const coefficients: number[] = Array.from({ length: columns.length }, () => 0);

    for (let row = columns.length - 1; row >= 0; row -= 1) {
      let remainder = projectedTarget[row];
      for (let column = row + 1; column < columns.length; column += 1) {
        remainder -= upperTriangle[row][column] * coefficients[column];
      }
      coefficients[row] = remainder / upperTriangle[row][row];
    }

    return coefficients;
  });
}

/**
 * Transform a pixel coordinate to map coordinates using the affine matrix.
 */
export function transformPoint(matrix: AffineMatrix, px: number, py: number): [number, number] {
  return [matrix.a * px + matrix.b * py + matrix.c, matrix.d * px + matrix.e * py + matrix.f];
}

/**
 * Compute the 4 corner coordinates of the image in map space (lng/lat).
 * The matrix operates in Mercator space, so the Y outputs are converted
 * back to latitude before returning.
 * Returns in the order expected by Mapbox GL image source:
 * [top-left, top-right, bottom-right, bottom-left]
 */
export function computeImageCorners(
  matrix: AffineMatrix,
  width: number,
  height: number
): [[number, number], [number, number], [number, number], [number, number]] {
  const corners = [
    transformPoint(matrix, 0, 0), // top-left
    transformPoint(matrix, width, 0), // top-right
    transformPoint(matrix, width, height), // bottom-right
    transformPoint(matrix, 0, height), // bottom-left
  ] as [[number, number], [number, number], [number, number], [number, number]];

  for (const corner of corners) {
    corner[1] = latFromMercatorY(corner[1]);
  }

  return corners;
}

/**
 * Compute the RMS residual error of the affine transformation across all GCPs.
 * Returns the root mean square of the Euclidean distances between
 * predicted and actual coordinates in Mercator space.
 */
export function computeResidualError(matrix: AffineMatrix, gcps: GCPInput[]): number {
  if (gcps.length === 0) return 0;

  let sumSqError = 0;
  for (const gcp of gcps) {
    const [predLng, predMercY] = transformPoint(matrix, gcp.imageX, gcp.imageY);
    const dLng = predLng - gcp.mapLng;
    const dMercY = predMercY - mercatorYFromLat(gcp.mapLat);
    sumSqError += dLng * dLng + dMercY * dMercY;
  }

  return Math.sqrt(sumSqError / gcps.length);
}
